// Acceso generico a la API REST de Twenty (/rest/<objeto>).
// Formatos: GET lista -> { data: { <plural>: [...] }, pageInfo, totalCount }
//           GET uno   -> { data: { <singular>: {...} } }
//           POST      -> { data: { create<Singular>: {...} } }
//           PATCH     -> { data: { update<Singular>: {...} } }
//           DELETE    -> { data: { delete<Singular>: { id } } }

import { request } from './http';

export type ListParams = {
  filter?: string;
  orderBy?: string;
  limit?: number;
  depth?: 0 | 1;
  after?: string;
  signal?: AbortSignal;
};

export type Page<T> = { items: T[]; hasNextPage: boolean; endCursor?: string; total?: number };

type ListResponse = {
  data: Record<string, unknown[]>;
  pageInfo?: { hasNextPage?: boolean; endCursor?: string };
  totalCount?: number;
};

export async function list<T>(plural: string, p: ListParams = {}): Promise<Page<T>> {
  const res = await request<ListResponse>(`/rest/${plural}`, {
    signal: p.signal,
    query: {
      filter: p.filter,
      order_by: p.orderBy,
      limit: p.limit ?? 60,
      depth: p.depth,
      starting_after: p.after,
    },
  });
  const items = (Object.values(res.data ?? {})[0] ?? []) as T[];
  return { items, hasNextPage: !!res.pageInfo?.hasNextPage, endCursor: res.pageInfo?.endCursor, total: res.totalCount };
}

// Trae varias paginas seguidas (para tableros y agregados). Tope para no saturar el servidor.
export async function listAll<T>(plural: string, p: ListParams = {}, max = 1000): Promise<T[]> {
  const out: T[] = [];
  let after: string | undefined;
  do {
    const page = await list<T>(plural, { ...p, limit: Math.min(200, max - out.length), after });
    out.push(...page.items);
    after = page.hasNextPage ? page.endCursor : undefined;
  } while (after && out.length < max);
  return out;
}

const first = <T>(res: { data: Record<string, unknown> }) => Object.values(res.data ?? {})[0] as T;

export async function get<T>(plural: string, id: string, depth: 0 | 1 = 1): Promise<T> {
  return first<T>(await request(`/rest/${plural}/${id}`, { query: { depth } }));
}

export async function create<T>(plural: string, data: Record<string, unknown>): Promise<T> {
  return first<T>(await request(`/rest/${plural}`, { method: 'POST', body: data }));
}

export async function update<T>(plural: string, id: string, data: Record<string, unknown>): Promise<T> {
  return first<T>(await request(`/rest/${plural}/${id}`, { method: 'PATCH', body: data }));
}

export async function remove(plural: string, id: string) {
  return first<{ id: string }>(await request(`/rest/${plural}/${id}`, { method: 'DELETE' }));
}

// Utilidades para armar filtros de Twenty: campo[op]:valor, and(...), or(...).
export const q = (s: string) => `"${String(s).replace(/"/g, '\\"')}"`;
export const f = {
  eq: (campo: string, v: string | number | boolean) => `${campo}[eq]:${typeof v === 'string' ? q(v) : v}`,
  neq: (campo: string, v: string) => `${campo}[neq]:${q(v)}`,
  in: (campo: string, vs: string[]) => `${campo}[in]:[${vs.map(q).join(',')}]`,
  gte: (campo: string, v: string) => `${campo}[gte]:${q(v)}`,
  lte: (campo: string, v: string) => `${campo}[lte]:${q(v)}`,
  lt: (campo: string, v: string) => `${campo}[lt]:${q(v)}`,
  ilike: (campo: string, v: string) => `${campo}[ilike]:${q(`%${v}%`)}`,
  like: (campo: string, v: string) => `${campo}[like]:${q(`%${v}%`)}`,
  isNull: (campo: string) => `${campo}[is]:NULL`,
  notNull: (campo: string) => `${campo}[is]:NOT_NULL`,
  and: (...fs: (string | false | undefined | null)[]) => {
    const v = fs.filter(Boolean) as string[];
    return v.length ? (v.length === 1 ? v[0] : `and(${v.join(',')})`) : undefined;
  },
  or: (...fs: (string | false | undefined | null)[]) => {
    const v = fs.filter(Boolean) as string[];
    return v.length ? (v.length === 1 ? v[0] : `or(${v.join(',')})`) : undefined;
  },
};
